import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  debounceTime,
  filter,
  finalize,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { PromoCodesService } from '../../../+promo/services/promocodes.service';
import { UserService } from '../../../../@shared/services/user.service';
import { PromoCodesHistoryDataSource } from './promo-codes-history.data-source';
import { PromoInfoModalComponent } from './promo-info-modal/promo-info-modal.component';
import { IPromoCodeLog } from '../../../+promo/interfaces/promo-code-log.interface';
import { IPromoCodeChainStep } from '../../../+promo/interfaces/promo-code-chain-step.interface';
import { IPromoCodeChainUserState } from '../../../+promo/interfaces/promo-code-chain-user-state.interface';
import { AuthUserService } from '../../../../@shared/services/auth/auth-user.service';
import { ManualPromoActivationDialogComponent } from './manual-promo-activation-dialog/manual-promo-activation-dialog.component';

@Component({
  selector: 'app-promo-codes-history',
  templateUrl: './promo-codes-history.component.html',
  styleUrls: ['./promo-codes-history.component.scss']
})
export class PromoCodesHistoryComponent implements AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator, { static: false }) paginator!: MatPaginator;

  // Колонки 1-в-1 с promo_codes_log (фильтр всегда по текущему юзеру).
  private readonly legacyColumns: string[] = [
    'id',
    'code_id',
    'timestamp_used',
    'activated',
    'timestamp_activated',
    'closed_at',
    'deposit_sum',
    'reward_value',
    'payback_amount',
    'transaction_id',
    'next_promo_id'
  ];

  public get columns(): string[] {
    if (!this.logSource.supportsChainHistory$.value) {
      return this.withActions(this.legacyColumns);
    }
    return this.withActions([
      'id',
      'code_id',
      'chain',
      'chain_cycle',
      'chain_step',
      ...this.legacyColumns.slice(2)
    ]);
  }

  public logSource = new PromoCodesHistoryDataSource(this.promoCodesService);
  private readonly destroyed$: Subject<void> = new Subject<void>();
  private readonly manualActivationRoles: string[] = [
    'main_promoter',
    'main_admin',
    'main_owner'
  ];
  public canActivatePromoCodes: boolean = false;
  public activatingLogId: number | null = null;

  public filterForm: FormGroup = this.fb.group({
    codeId: null,
    activated: null,
    minActivatedAt: null,
    maxActivatedAt: null
  });

  constructor(
    private promoCodesService: PromoCodesService,
    private userService: UserService,
    private authUserService: AuthUserService,
    private fb: FormBuilder,
    private readonly dialog: MatDialog
  ) {
    this.authUserService.user$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user: any) => {
        this.canActivatePromoCodes =
          user?.roles?.some((role: string) =>
            this.manualActivationRoles.includes(role)
          ) || false;
      });
    this.loadLogs();
  }

  public canManuallyActivate(log: IPromoCodeLog): boolean {
    return (
      this.canActivatePromoCodes &&
      !log.activated &&
      log.next_promo_id == null &&
      log.promoCode != null
    );
  }

  public getTimestampUsedUtc(timestampUsed: string): Date | null {
    const normalized = timestampUsed.trim().replace(' ', 'T');
    const hasTimezone = /(Z|[+-]\d{2}(?::?\d{2})?)$/i.test(normalized);
    const timestamp = new Date(
      hasTimezone ? normalized : `${normalized}Z`
    ).getTime();
    if (Number.isNaN(timestamp)) {
      return null;
    }

    // timestamp_used хранится в UTC, но legacy API возвращает его без timezone.
    return new Date(timestamp);
  }

  public activatePromoCode(log: IPromoCodeLog): void {
    if (!this.canManuallyActivate(log)) {
      return;
    }

    const minDepositSum = log.promoCode?.min_deposit_sum ?? 0;
    this.dialog
      .open(ManualPromoActivationDialogComponent, {
        width: '520px',
        maxWidth: '95vw',
        autoFocus: false,
        data: {
          code: log.promoCode?.code || `ID ${log.code_id}`,
          minDepositSum
        }
      })
      .afterClosed()
      .pipe(
        filter(
          (depositAmount: unknown): depositAmount is number =>
            Number.isInteger(depositAmount)
        ),
        switchMap(depositAmount => {
          this.activatingLogId = log.id;
          return this.promoCodesService
            .activatePromoCodeManually(
              log.id,
              this.userService.currentUserId.getValue(),
              depositAmount
            )
            .pipe(finalize(() => (this.activatingLogId = null)));
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => this.loadLogs());
  }

  /** Открывает карточку промокода по id (code_id или next_promo_id). */
  openPromoInfo(id: number | null): void {
    if (id == null) {
      return;
    }
    this.dialog.open(PromoInfoModalComponent, {
      width: '560px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: { id }
    });
  }

  getChainState(log: IPromoCodeLog): IPromoCodeChainUserState | undefined {
    return this.logSource.chainStates$.value.find(state =>
      state.campaign?.chainSteps?.some(
        step => step.promoCode?.id === log.code_id
      )
    );
  }

  getChainStep(log: IPromoCodeLog): number | null {
    const state = this.getChainState(log);
    return (
      state?.campaign?.chainSteps?.find(
        step => step.promoCode?.id === log.code_id
      )
        ?.step ?? null
    );
  }

  getChainCycle(log: IPromoCodeLog): number | null {
    const depositUse = log.chainDepositUses?.[0];
    if (depositUse) {
      return depositUse.cycleNo;
    }
    const state = this.getChainState(log);
    return state && this.getChainStep(log) === state.currentStep
      ? state.cycleNo
      : null;
  }

  getOrderedSteps(state: IPromoCodeChainUserState): IPromoCodeChainStep[] {
    return [...(state.campaign?.chainSteps || [])].sort(
      (left, right) => left.step - right.step
    );
  }

  isCompletedStep(state: IPromoCodeChainUserState, step: number): boolean {
    return state.status === 'completed' || step < state.currentStep;
  }

  getChainStatus(state: IPromoCodeChainUserState): string {
    if (state.status === 'completed') {
      return 'Завершена';
    }
    return new Date(state.deadlineAt).getTime() < Date.now()
      ? 'Дедлайн истёк'
      : 'Активна';
  }

  getChainCountLabel(count: number): string {
    const lastTwoDigits = count % 100;
    const lastDigit = count % 10;
    if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
      return 'цепочек';
    }
    if (lastDigit === 1) {
      return 'цепочка';
    }
    return lastDigit >= 2 && lastDigit <= 4 ? 'цепочки' : 'цепочек';
  }

  trackByChainState(
    _index: number,
    state: IPromoCodeChainUserState
  ): number {
    return state.campaign.id;
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe(() => this.loadLogs());

    this.filterForm.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroyed$))
      .subscribe(() => {
        if (this.paginator) {
          this.paginator.pageIndex = 0;
        }
        this.loadLogs();
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private buildParams(): any {
    const form = this.filterForm.value;
    return {
      userIds: [this.userService.currentUserId.getValue()],
      ...(form.codeId ? { codeIds: [form.codeId] } : {}),
      ...(form.activated !== null &&
      form.activated !== undefined &&
      form.activated !== ''
        ? { activated: form.activated }
        : {}),
      ...(form.minActivatedAt ? { minActivatedAt: form.minActivatedAt } : {}),
      ...(form.maxActivatedAt ? { maxActivatedAt: form.maxActivatedAt } : {})
    };
  }

  private withActions(columns: string[]): string[] {
    return this.canActivatePromoCodes ? [...columns, 'actions'] : columns;
  }

  loadLogs(): void {
    this.logSource.loadData(
      this.paginator?.pageIndex || 0,
      this.paginator?.pageSize || 10,
      this.buildParams()
    );
  }
}
